// Shared display helpers for wallet↔figure linkages. We surface EVERY linkage
// (confirmed attribution + softer leads like a GMGN vendor label); these render
// the confidence and origin legibly instead of hiding non-confirmed links.

export type LinkEvidence = {
  type: string;
  addedBy?: string;
  confidence?: string;
  source?: string | null;
  basis?: string | null;
  vendor?: string | null;
  vendorValue?: string | null;
};

/** Tailwind classes for a confidence chip. */
export const confChip = (c: string): string =>
  c === 'confirmed' ? 'border-crypto-green/40 text-crypto-green'
    : c === 'probable' ? 'border-amber-400/40 text-amber-400'
      : 'border-gray-600 text-gray-400';

export const EV_LABEL: Record<string, string> = {
  research_pipeline: 'deepnets',
  onchain_funding: 'on-chain funding',
  onchain_deployer: 'on-chain deploy',
  self_disclosed: 'self-disclosed',
  vendor_label: 'vendor',
  third_party_claim: 'third-party',
  other: 'other',
};

const CONF_ORDER: Record<string, number> = { confirmed: 3, probable: 2, claimed: 1 };

/** The strongest piece of evidence (highest confidence). */
export const topEvidence = (evidence: LinkEvidence[] = []): LinkEvidence | null =>
  [...evidence].sort((a, b) => (CONF_ORDER[b.confidence || 'claimed'] || 0) - (CONF_ORDER[a.confidence || 'claimed'] || 0))[0] ?? null;

/** Short "via X" origin label — vendor name for vendor labels, else the evidence kind. */
export const linkProvenance = (evidence: LinkEvidence[] = []): string => {
  const top = topEvidence(evidence);
  if (!top) return '';
  if (top.type === 'vendor_label' && top.vendor) return top.vendor.toUpperCase();
  return EV_LABEL[top.type] || top.type;
};

/** Full evidence trail for a tooltip. */
export const evidenceTitle = (evidence: LinkEvidence[] = []): string =>
  evidence
    .map(e => `${EV_LABEL[e.type] || e.type}${e.vendor ? ` (${e.vendor}${e.vendorValue ? `: ${e.vendorValue}` : ''})` : ''} — ${e.confidence || '?'}`)
    .join('\n');
